import type { RiskScore } from '../types';

interface RiskScoreGaugeProps {
  riskScore: RiskScore;
  size?: 'sm' | 'md' | 'lg';
}

const sizeConfig = {
  sm: { width: 100, strokeWidth: 8, fontSize: 'text-2xl' },
  md: { width: 160, strokeWidth: 12, fontSize: 'text-4xl' },
  lg: { width: 200, strokeWidth: 14, fontSize: 'text-5xl' },
};

export default function RiskScoreGauge({ riskScore, size = 'md' }: RiskScoreGaugeProps) {
  const config = sizeConfig[size];
  const radius = (config.width - config.strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (riskScore.score / 100) * circumference;

  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: config.width, height: config.width }}>
        <svg
          width={config.width}
          height={config.width}
          viewBox={`0 0 ${config.width} ${config.width}`}
          className="transform -rotate-90"
        >
          {/* Background circle */}
          <circle
            cx={config.width / 2}
            cy={config.width / 2}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={config.strokeWidth}
            className="text-void-lighter"
          />
          {/* Score circle */}
          <circle
            cx={config.width / 2}
            cy={config.width / 2}
            r={radius}
            fill="none"
            stroke={riskScore.color}
            strokeWidth={config.strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className="gauge-circle transition-all duration-1000"
          />
        </svg>
        {/* Score text */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span
            className={`${config.fontSize} font-tech font-bold`}
            style={{ color: riskScore.color }}
          >
            {riskScore.score}
          </span>
          <span
            className="text-xs font-semibold uppercase tracking-wider"
            style={{ color: riskScore.color }}
          >
            {riskScore.label}
          </span>
        </div>
      </div>

      {/* Stats below gauge */}
      {size !== 'sm' && (
        <div className="flex gap-6 mt-4 text-center">
          <div>
            <p className="text-2xl font-tech font-bold text-white">
              {riskScore.breachCount}
            </p>
            <p className="text-xs text-gray-500">Breaches</p>
          </div>
          <div>
            <p className="text-2xl font-tech font-bold text-signal-red">
              {riskScore.criticalCount}
            </p>
            <p className="text-xs text-gray-500">Critical</p>
          </div>
          <div>
            <p className="text-2xl font-tech font-bold text-signal-yellow">
              {riskScore.recentCount}
            </p>
            <p className="text-xs text-gray-500">Recent</p>
          </div>
        </div>
      )}
    </div>
  );
}
